/**
 * Demo-tab only: turns our orange into the reseller's colour everywhere in the real dashboard, without
 * touching its code (Martin, 2026-09-24: the brand colour must show in the customer dashboard too).
 *
 * Every colour of the orange family (hue 8–38°, saturated) in every stylesheet rule — tokens, hard-coded
 * hex, Tailwind arbitrary classes, gradients, shadows — and in inline style/fill/stroke is re-hued to the
 * brand colour. Lightness is kept for tints (washes stay washes) and shifted towards the brand's own
 * lightness for mid-tones, so a dark teal stays dark. New stylesheets and elements are watched and
 * re-tinted as routes load. The real fix is tenant-branding's token pass; this is the demo's bridge.
 */
type RGB = [number, number, number];

const ORANGE_L = 0.52; // lightness of #ff5a0a

function rgbToHsl([r, g, b]: RGB): [number, number, number] {
  const [R, G, B] = [r / 255, g / 255, b / 255];
  const max = Math.max(R, G, B), min = Math.min(R, G, B);
  const l = (max + min) / 2;
  if (max === min) return [0, 0, l];
  const d = max - min;
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
  const h = max === R ? ((G - B) / d + (G < B ? 6 : 0)) : max === G ? (B - R) / d + 2 : (R - G) / d + 4;
  return [h * 60, s, l];
}

function hslToRgb(h: number, s: number, l: number): RGB {
  const k = (n: number) => (n + h / 30) % 12;
  const a = s * Math.min(l, 1 - l);
  const f = (n: number) => l - a * Math.max(-1, Math.min(k(n) - 3, Math.min(9 - k(n), 1)));
  return [Math.round(f(0) * 255), Math.round(f(8) * 255), Math.round(f(4) * 255)];
}

export function hexToRgb(hex: string): RGB | null {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex.trim());
  if (!m) return null;
  return [0, 2, 4].map((i) => parseInt(m[1].slice(i, i + 2), 16)) as RGB;
}

export function makeRetinter(brandHex: string) {
  const brand = hexToRgb(brandHex);
  if (!brand) return null;
  const [bh, bs, bl] = rgbToHsl(brand);
  const cache = new Map<string, RGB | null>();

  const retint = (rgb: RGB): RGB | null => {
    const key = rgb.join(',');
    if (cache.has(key)) return cache.get(key)!;
    const [h, s, l] = rgbToHsl(rgb);
    let out: RGB | null = null;
    if (h >= 8 && h <= 38 && s >= 0.55 && l > 0.18 && l < 0.975) {
      const weight = Math.max(0, 1 - Math.abs(l - ORANGE_L) / 0.48);
      const nl = Math.min(0.98, Math.max(0.02, l + (bl - ORANGE_L) * weight));
      out = hslToRgb(bh, Math.min(1, bs * Math.min(1, s / 0.9)), nl);
    }
    cache.set(key, out);
    return out;
  };

  const hex2 = (n: number) => n.toString(16).padStart(2, '0');

  /** Rewrites every orange colour inside a CSS value; returns null when nothing changed. */
  const value = (v: string): string | null => {
    let changed = false;
    let out = v.replace(/#([0-9a-f]{3,4}|[0-9a-f]{6}|[0-9a-f]{8})\b/gi, (m, hx: string) => {
      const full = hx.length <= 4 ? hx.split('').map((c) => c + c).join('') : hx;
      const rgb = [0, 2, 4].map((i) => parseInt(full.slice(i, i + 2), 16)) as RGB;
      const t = retint(rgb);
      if (!t) return m;
      changed = true;
      return `#${t.map(hex2).join('')}${full.length === 8 ? full.slice(6) : ''}`;
    });
    out = out.replace(/(rgba?\(\s*)(\d{1,3})(\s*,\s*|\s+)(\d{1,3})(\s*,\s*|\s+)(\d{1,3})/gi, (m, pre, r, s1, g, s2, b) => {
      const t = retint([Number(r), Number(g), Number(b)]);
      if (!t) return m;
      changed = true;
      return `${pre}${t[0]}${s1}${t[1]}${s2}${t[2]}`;
    });
    return changed ? out : null;
  };

  return { value };
}

type Retinter = NonNullable<ReturnType<typeof makeRetinter>>;

function retintStyle(style: CSSStyleDeclaration, t: Retinter) {
  for (let i = 0; i < style.length; i++) {
    const prop = style[i];
    const v = style.getPropertyValue(prop);
    if (!v) continue;
    const next = t.value(v);
    if (next !== null) style.setProperty(prop, next, style.getPropertyPriority(prop));
  }
}

function retintRules(rules: CSSRuleList, t: Retinter) {
  for (const rule of Array.from(rules)) {
    if ('style' in rule && (rule as CSSStyleRule).style) retintStyle((rule as CSSStyleRule).style, t);
    if ('cssRules' in rule && (rule as CSSGroupingRule).cssRules) retintRules((rule as CSSGroupingRule).cssRules, t);
  }
}

function retintElement(el: Element, t: Retinter) {
  if (el instanceof HTMLElement || el instanceof SVGElement) {
    if (el.getAttribute('style')) retintStyle(el.style, t);
    for (const attr of ['fill', 'stroke', 'stop-color', 'color']) {
      const v = el.getAttribute(attr);
      const next = v ? t.value(v) : null;
      if (next !== null) el.setAttribute(attr, next);
    }
  }
}

/** Starts re-tinting the document and keeps doing so as styles and elements are added. */
export function startRecolor(brandHex: string): void {
  const t = makeRetinter(brandHex);
  if (!t) return;
  const done = new WeakSet<CSSStyleSheet>();

  const sheets = () => {
    for (const sheet of Array.from(document.styleSheets)) {
      if (done.has(sheet)) continue;
      try { retintRules(sheet.cssRules, t); done.add(sheet); } catch { /* cross-origin or not loaded yet */ }
    }
  };
  const subtree = (root: Element) => { retintElement(root, t); root.querySelectorAll('[style],[fill],[stroke],[stop-color]').forEach((el) => retintElement(el, t)); };

  let queued = false;
  const flush = () => { queued = false; sheets(); };
  const schedule = () => { if (!queued) { queued = true; requestAnimationFrame(flush); } };

  const start = () => {
    sheets();
    subtree(document.documentElement);
    new MutationObserver((records) => {
      for (const r of records) {
        if (r.type === 'attributes' && r.target instanceof Element) retintElement(r.target, t);
        r.addedNodes.forEach((n) => {
          if (n instanceof HTMLStyleElement || n instanceof HTMLLinkElement) { n.addEventListener('load', schedule); schedule(); }
          else if (n instanceof Element) subtree(n);
        });
        if (r.type === 'characterData' || (r.target instanceof HTMLStyleElement)) schedule();
      }
    }).observe(document.documentElement, { subtree: true, childList: true, characterData: true, attributes: true, attributeFilter: ['style', 'fill', 'stroke', 'stop-color'] });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true }); else start();
}
